import React, { useEffect, useLayoutEffect, useState } from "react";
import { Dimensions, KeyboardAvoidingView, Platform, StyleSheet, TouchableOpacity, View, Image } from "react-native";
import { MD3Theme, useTheme } from "react-native-paper";
import i18n from "../../../i18n";
import Mutations from "../../../api/mutations";
import { mobUser } from "../../../mobx/MobUser";
import { mobNotif } from "../../../mobx/MobNotifs";
import * as ImagePicker from "expo-image-picker";
import { Camera } from "expo-camera";
import { GiftedChat, Bubble, Actions, ActionsProps, InputToolbar, Composer } from "react-native-gifted-chat";
import SendButton from "../../../components/SendButton";
import getTextFromMultilang from "../../../utils/getTextFromMultiLang";
import { observer } from "mobx-react";
import CustomHeader from "../../../navigation/CustomHeader";
import { mobAlert } from "../../../mobx/MobAlert";
import { FontAwesome } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import { SafeAreaView } from "react-native-safe-area-context";

const Chat = observer(() => {

  const theme = useTheme();
  const styles = styleGen(theme);
  const [messages, setMessages] = useState([]);
  const [isPortrait, setIsPortrait] = useState<boolean>(true);
  const mutations = new Mutations();

  const navigation = useNavigation();

  useLayoutEffect(() => {
    const parent = navigation.getParent();
    parent?.setOptions({
      tabBarStyle: { display: 'none' },
    });

    return () => {
      parent?.setOptions({
        tabBarStyle: undefined,
      });
    };
  }, [navigation]);

  useEffect(() => {
    mobNotif.setChatSub(mobUser.OiqiaBookingId);
    mobNotif.isInChat = true;
    checkIfPortrait();
    return () => {
      mobNotif.resetChat();
      mobNotif.isInChat = false;
    };
  }, []);

  useEffect(() => {
    if (mobNotif.chatData[0]?.conversation) {
      const newConversation = mobNotif.chatData[0].conversation.map(message => {
        const translatedText = message.textMulti ? getTextFromMultilang(mobUser.Language, message.textMulti) : message.text;
        return {
          ...message,
          text: translatedText
        };
      });
      setMessages(newConversation);
    }
  }, [mobNotif.chatData, mobUser.Language]);

  const sendMessage = async (newMessages) => {
    const bookingIdOiqia = mobUser.OiqiaBookingId;
    const { text, user, image } = newMessages[0];

    const NewMessage = {
      bookingIdOiqia,
      text,
      image,
      user
    };

    try {
      await mutations.setIncidentMsg(NewMessage);
    } catch (_err) {
      console.log("ERROR setIncidentMsg", _err);
      console.log("ERROR setIncidentMsg", _err.networkError.result.errors);
    }
  };

  const USER = {
    "_id": "traveler",
    "name": "Locataire",
    "avatar": ""
  };

  const checkIfPortrait = () => {
    const dim = Dimensions.get("screen");
    setIsPortrait(dim.height >= dim.width);
  };

  Dimensions.addEventListener("change", checkIfPortrait);

  // Fonction pour demander les permissions caméra et ouvrir
  const takePictureWithPermission = async () => {
    try {
      // Demander permission caméra
      const cameraPermission = await Camera.requestCameraPermissionsAsync();

      if (!cameraPermission.granted) {
        alert(i18n.t("permissions.MSG_PERMISSION_CAMERA") || "Permission caméra requise");
        return;
      }

      // Si permission accordée, ouvrir la caméra
      mobAlert.openCamera(sendPicture, isPortrait);

    } catch (error) {
      console.log("Erreur permission caméra:", error);
      alert(i18n.t("ERROR_CAMERA_PERMISSION") || "Erreur lors de l'accès à la caméra");
    }
  };

  // Fonction pour demander les permissions galerie et ouvrir
  const pickImageWithPermission = async () => {
    try {
      // Demander permission galerie
      const galleryPermission = await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (!galleryPermission.granted) {
        alert(i18n.t("permissions.MSG_PERMISSION_LIBRARY") || "Permission galerie requise");
        return;
      }

      // Si permission accordée, ouvrir la galerie
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        base64: true,
        quality: 0.5,
      });

      if (!result.canceled) {
        sendPicture(result.assets[0]);
      }

    } catch (error) {
      console.log("Erreur permission galerie:", error);
      alert(i18n.t("ERROR_GALLERY_PERMISSION") || "Erreur lors de l'accès à la galerie");
    }
  };

  const renderActions = (props: Readonly<ActionsProps>) => {
    return (
      <Actions
        {...props}
        icon={() => (
          <FontAwesome name="plus" size={16} color="#fff" />
        )}
        containerStyle={{
          justifyContent: 'center',
          alignItems: 'center',
          borderColor: "#fff",
          borderWidth: 1,
          borderRadius: 20,
        }}
        options={{
          [i18n.t("generic.takePhoto") || "Camera"]: takePictureWithPermission,
          [i18n.t("generic.openGallery") || "Gallery"]: pickImageWithPermission,
          [i18n.t("generic.cancel") || "Cancel"]: () => null,
        }}
      />
    );
  }

  // Fonction pour envoyer une image
  const sendPicture = async (picture) => {
    try {
      const link = await mutations.uploadComplaintImage(
        mobUser.OiqiaBookingId,
        `data:image/jpeg;base64,${picture.base64}`,
      );

      const msg = {
        text: "",
        image: link.data.uploadComplaintImage,
        user: USER,
        conversationId: mobUser.conversationId
      };

      sendMessage([msg]);
    } catch (_err) {
      alert(i18n.t("ERROR_CHAT_BOT_TITLE") + " " + i18n.t("ERROR_CHAT_BOT_DESCRIPTION"));
    }
  };

  const renderInputToolBar = (props) => {
    return (
      <InputToolbar
        {...props}
        containerStyle={{
          borderRadius: 16,
          borderColor: "#10632B",
          marginHorizontal: 8,
          marginTop: 5,
          backgroundColor: "grey",
          borderTopWidth: 0
        }}
      />
    );
  };

  const renderBubble = (props) => (
    <Bubble
      {...props}
      textStyle={{
        right: {
          color: theme.colors.background,
        },
      }}
      wrapperStyle={{
        left: {
          backgroundColor: theme.colors.secondary,
        },
        right: {
          backgroundColor: theme.colors.primary,
        }
      }}
    />
  );

  const renderComposer = (props) => (
    <Composer
      {...props}
      placeholderTextColor="#fff"
      textInputStyle={{
        color: '#fff',
        textAlignVertical: 'center',
        paddingBottom: 10,
        fontSize: 16,
      }}
    />
  );

  const renderMessageImage = (props) => {
    const imageUri = props?.currentMessage?.image;
    return (
      <TouchableOpacity onPress={() => mobAlert.setImageModal(0, [{ url: imageUri }])}>
        <Image
          source={{ uri: imageUri }}
          style={{ width: 150, height: 150, borderRadius: 8, margin: 5 }}
          resizeMode="cover"
        />
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.Background} edges={["bottom"]}>
      <View style={styles.androidTopHeader}>
        <CustomHeader
          text={i18n.t("home.chat")}
          hasArrow={true}
          theme={theme}
        />
      </View>

      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        keyboardVerticalOffset={0}
      >
        <GiftedChat
          messages={messages}
          onSend={sendMessage}
          user={USER}
          locale={mobUser.Language}
          showUserAvatar={false}
          scrollToBottom
          bottomOffset={Platform.OS === "ios" ? -10 : 0}
          placeholder={i18n.t("pages.chat.placeholder")}
          keyboardShouldPersistTaps="handled"
          renderSend={props => <SendButton style={{ color: "#fff" }} {...props} />}
          renderActions={renderActions}
          renderInputToolbar={renderInputToolBar}
          renderBubble={renderBubble}
          renderComposer={renderComposer}
          renderMessageImage={renderMessageImage}
          messagesContainerStyle={styles.messagesContainerStyle}
        />
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
});

const styleGen = (theme: MD3Theme) => StyleSheet.create({
  Background: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  androidTopHeader: {
    backgroundColor: "#E00078",
  },
  messagesContainerStyle: {
    backgroundColor: "#FFFFFFE6",
  },
  keyboardAvoidingView: {
    flex: 1,
  },
});

export default Chat;